import { CRUDServiceBase } from '@lib/Http'
import {
  ECategoryType,
  EMediaType,
  EPriceType,
  ICategory,
  IExploreFilter,
  IExploreFilterPagination,
  IGallery,
  IPrice,
  IProduct,
  IVideoStream
} from '@shared/Types'
import Graphql from '@coreprj/graphql'

type TExploreMedia = Graphql.QMediaStore.TExploreMedia
type TCategory = Graphql.QMediaStore.TCategory
type TPricePackage = Graphql.QMediaStore.TPricePackage

class ExploreMediaServiceBase extends CRUDServiceBase<IGallery, number> {
  defaultFilterPagination: Required<IExploreFilterPagination> = { page: 1, pageSize: 24, total: 0 }

  constructor() {
    super(`/api/v1/guest`)
  }

  initialWithGraphql = async (filter: IExploreFilter, signal?: AbortSignal) => {
    const fRequest = this.convertFilterToRequest(filter)
    const [res, resCount] = await Promise.all([
      Graphql.GeustMediaStore.Query(Graphql.QMediaStore.ExplorePageWithMedia(fRequest.media, fRequest.category), { signal }),
      Graphql.GeustMediaStore.Query(Graphql.QMediaStore.ExploreCountWithMedia(), { signal })
    ])
    return {
      medias: this.mapProducts(res.mediaStore.medias?.items as TExploreMedia[]),
      totalMedias: resCount.mediaStore.medias?.totalItems ?? 0,
      categories: this.mapCategories(res.mediaStore.categories?.items as TCategory[]),
      prices: this.mapPrices(res.mediaStore.pricePackages?.items as TPricePackage[])
    }
  }

  allWithGraphql = async (filter: IExploreFilter, signal?: AbortSignal) => {
    const fRequest = this.convertFilterToRequest(filter)
    const res = await Graphql.GeustMediaStore.Query(Graphql.QMediaStore.ExploreFilterWithMedia(fRequest.media, fRequest.category), { signal })
    return {
      medias: this.mapProducts(res.mediaStore.medias?.items as TExploreMedia[]),
      totalMedias: res.mediaStore.medias?.totalItems ?? 0,
      categories: this.mapCategories(res.mediaStore.categories?.items as TCategory[])
    }
  }

  detailPageById = async (id: string, filter: IExploreFilter, signal?: AbortSignal) => {
    const fRequest = this.convertFilterToRequest(filter, id)
    const res = await Graphql.GeustMediaStore.Query(Graphql.QMediaStore.ExploreMediaDetailById({ id, categories: fRequest.category }), { signal })
    return {
      media: this.mapProduct(res.mediaStore.media as TExploreMedia),
      medias: this.mapProducts(res.mediaStore.randomMedias?.items as TExploreMedia[]),
      totalMedias: res.mediaStore.randomMedias?.totalItems ?? 0,
      categories: this.mapCategories(res.mediaStore.categories?.items as TCategory[]),
      prices: this.mapPrices(res.mediaStore.pricePackages?.items as TPricePackage[])
    }
  }

  private convertFilterToRequest = (filter: IExploreFilter, currentId?: string) => {
    const { keyword, categories } = filter
    const mediaFilter = Graphql.createRequestBuilder<TExploreMedia>({ ignoreEmpty: false })
    const categoryFilter = Graphql.createRequestBuilder<TPricePackage>({ ignoreEmpty: false })
    const pageSize = filter.pagination?.pageSize || this.defaultFilterPagination.pageSize
    const page = (filter.pagination?.page || this.defaultFilterPagination.page) - 1
    mediaFilter.take(pageSize).skip(page * pageSize)

    if (!!filter.type) {
      mediaFilter.filterEnum('type', 'MediaType.' + filter.type)
      categoryFilter.filterEnum('type', 'CategoryType.' + filter.type)
    }

    if (!!keyword) {
      mediaFilter.scope((q) => q.filterContains('name', keyword).filterContains('description', keyword), { logic: 'And' })
    }

    const existingCategories = categories?.filter((x) => !!x.value) ?? []
    if (!!existingCategories.length) {
      mediaFilter.scope(
        (q) => {
          existingCategories.forEach((c) => q.filterCustom(`categories.any(name=="${c.value}")`))
          return q
        },
        { logic: 'And' }
      )
    }

    categoryFilter.sort('name', { direction: 'ASC' })

    // mediaFilter.sort("description",)

    if (!!filter.sort) mediaFilter.sort('dateCreated', { direction: filter.sort === 'Lastet' ? 'DESC' : 'ASC' })

    if (!!currentId) mediaFilter.filterNotEquals('id', currentId, { logic: 'And' })

    return { media: mediaFilter.build(), category: categoryFilter.build() }
  }

  private videoStreamUrl = '/Upload/GetVideoStream/'
  fetchVideoStream = (resourceId: string, signal?: AbortSignal): Promise<IVideoStream[]> => {
    return this.Get(this.videoStreamUrl + resourceId, { signal })
  }

  getMediaImageUri = (resourceId: string = '') => `/api/v1/guest/Upload/VideoThumb/${resourceId}`

  mapProducts = (items?: TExploreMedia[]): IProduct[] => {
    return (items || []).reduce<IProduct[]>((a, b) => {
      if (!!b?.id) a.push(this.mapProduct(b))
      return a
    }, [])
  }

  mapProduct = (item: TExploreMedia): IProduct => ({
    Id: item.id ?? '',
    Title: item.name ?? '',
    Description: item.description ?? '',
    Type: item.type as EMediaType,
    DeliveryId: item.deliveryId ?? '',
    ResourceId: item.resourceId ?? '',
    PublishedDate: item.dateCreated,
    ImageUri: this.getMediaImageUri(item.resourceId),
    Tags: item.categories?.map((x) => x?.name).join('|') ?? '',
    DateCreated: item.dateCreated,
    DateUpdated: item.dateUpdated
  })

  mapCategories = (items: TCategory[] = []): ICategory[] => {
    return items.map((item) => ({
      Id: item.id ?? '',
      Name: item.name ?? '',
      Description: item.description ?? '',
      ImageUri: item.imageUri ?? '',
      Type: item.type as ECategoryType,
      Counts: item.mediaCounts ?? 0,
      DateCreated: item.dateCreated
    }))
  }

  mapPrices = (items?: TPricePackage[]): IPrice[] => {
    if (!items) return []
    return items.reduce<IPrice[]>((a, b) => {
      a.push({
        Id: b.id,
        Name: b.name ?? '',
        Description: b.description ?? '',
        Price: b.price,
        Type: b.type as EPriceType,
        JsonContent: b.jsonContent ?? '',
        DateCreated: b.dateCreated
      })
      return a
    }, [])
  }
}
const ExploreMediaService = new ExploreMediaServiceBase()
export default ExploreMediaService
